/**
 * Product Archive Tabs & Accordion
 *
 * Handles tab navigation on desktop and accordion on mobile
 * for product category archives
 */

class ProductArchiveTabs {
    constructor() {
        this.init();
    }

    init() {
        this.setupTabNavigation();
        // Removed setupMobileAccordion() - let Bootstrap handle accordion
        this.syncTabsWithAccordion();
        this.handleResponsiveChanges();
        this.handleFilterPosition();
        this.setupSectionNavigation();
        this.setStickyHeaderHeight();
        this.setupDescriptionToggle();
    }

    /**
     * Set CSS variable for sticky header height
     */
    setStickyHeaderHeight() {
        const setHeight = () => {
            const header = document.querySelector('#main-header');
            const tabsNav = document.querySelector('.product-archive-nav');

            if (header && tabsNav) {
                const headerHeight = header.offsetHeight;
                // Set on document root so it's accessible everywhere
                document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
            }
        };

        // Set on load
        setHeight();

        // Update on resize
        window.addEventListener('resize', setHeight);
    }

    /**
     * Setup section navigation dropdown
     */
    setupSectionNavigation() {
        const dropdownItems = document.querySelectorAll('.section-navigation-dropdown .dropdown-item');

        dropdownItems.forEach(item => {
            item.addEventListener('click', (e) => {
                e.preventDefault();

                const targetId = item.getAttribute('data-target');
                const targetCollapse = document.querySelector(targetId);

                if (!targetCollapse) return;

                const accordionItem = targetCollapse.closest('.tab-pane');

                // Open the accordion first
                if (!targetCollapse.classList.contains('show')) {
                    $(targetCollapse).collapse('show');
                }

                // Wait for accordion to expand, then scroll to it with offset for header
                setTimeout(() => {
                    if (accordionItem) {
                        const headerHeight = document.querySelector('#main-header')?.offsetHeight || 100;
                        const elementPosition = accordionItem.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - headerHeight - 20; // 20px extra padding

                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 350);
            });
        });
    }

    /**
     * Move filter inside accordion on mobile
     */
    handleFilterPosition() {
        const moveFilter = () => {
            const filterWrapper = document.querySelector('.product-archive-tabs-wrapper .product-filter-column');
            const mobileTarget = document.querySelector('.mobile-filter-placeholder');

            if (!filterWrapper || !mobileTarget) return;

            if (window.innerWidth < 768) {
                // Move filter inside accordion on mobile
                if (mobileTarget.children.length === 0) {
                    mobileTarget.appendChild(filterWrapper);
                    // Ensure hide-filter items are hidden after moving
                    this.ensureHiddenFiltersAreHidden();
                }
            } else {
                // Move filter back to sidebar on desktop
                const tabsWrapper = document.querySelector('.product-archive-tabs-wrapper > .row');
                if (tabsWrapper && filterWrapper.parentElement !== tabsWrapper) {
                    // Insert as first child of row
                    tabsWrapper.insertBefore(filterWrapper, tabsWrapper.firstChild);
                    // Ensure hide-filter items are hidden after moving
                    this.ensureHiddenFiltersAreHidden();
                }
            }
        };

        // Run on load
        moveFilter();

        // Run on resize
        window.addEventListener('resize', moveFilter);
    }

    /**
     * Ensure hide-filter items are hidden on page load
     */
    ensureHiddenFiltersAreHidden() {
        // Small delay to ensure DOM is ready after move
        setTimeout(() => {
            const hideFilters = document.querySelectorAll('.product-category-filter .hide-filter');
            hideFilters.forEach(filter => {
                if (!filter.classList.contains('d-block')) {
                    filter.style.display = 'none';
                }
            });
        }, 50);
    }

    /**
     * Setup desktop tab navigation
     */
    setupTabNavigation() {
        const tabs = document.querySelectorAll('.product-archive-nav .nav-link');

        tabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();

                // Get target pane
                const targetId = tab.getAttribute('href');
                const targetPane = document.querySelector(targetId);

                if (!targetPane) return;

                // Remove active class from all tabs and panes
                const tabsWrapper = tab.closest('.product-archive-tabs-wrapper');
                tabsWrapper.querySelectorAll('.nav-link').forEach(t => {
                    t.classList.remove('active');
                    t.setAttribute('aria-selected', 'false');
                });
                tabsWrapper.querySelectorAll('.tab-pane').forEach(p => {
                    p.classList.remove('show', 'active');
                });

                // Add active class to clicked tab and target pane
                tab.classList.add('active');
                tab.setAttribute('aria-selected', 'true');
                targetPane.classList.add('show', 'active');

                // Scroll to product-tabs-column on desktop, accounting for header height
                if (window.innerWidth >= 768) {
                    const tabsColumn = document.querySelector('.product-tabs-column');
                    if (tabsColumn) {
                        const headerHeight = document.querySelector('#main-header')?.offsetHeight || 100;
                        const tabsColumnPosition = tabsColumn.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = tabsColumnPosition - headerHeight;

                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                    }
                }
            });
        });
    }

    /**
     * Setup mobile accordion behavior
     */
    setupMobileAccordion() {
        const accordionButtons = document.querySelectorAll('.product-archive-tabs-wrapper .accordion-button');

        accordionButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();

                const targetId = button.getAttribute('data-target');
                const targetCollapse = document.querySelector(targetId);

                if (!targetCollapse) return;

                // Toggle collapsed state
                const isExpanded = button.getAttribute('aria-expanded') === 'true';

                if (isExpanded) {
                    // Collapse
                    button.classList.add('collapsed');
                    button.setAttribute('aria-expanded', 'false');
                    this.collapseElement(targetCollapse);
                } else {
                    // Expand - first collapse all others in this wrapper
                    const wrapper = button.closest('.product-archive-tabs-wrapper');
                    wrapper.querySelectorAll('.accordion-button').forEach(btn => {
                        if (btn !== button) {
                            btn.classList.add('collapsed');
                            btn.setAttribute('aria-expanded', 'false');
                            const collapseId = btn.getAttribute('data-target');
                            const collapseEl = document.querySelector(collapseId);
                            if (collapseEl) {
                                this.collapseElement(collapseEl);
                            }
                        }
                    });

                    // Then expand clicked
                    button.classList.remove('collapsed');
                    button.setAttribute('aria-expanded', 'true');
                    this.expandElement(targetCollapse);
                }
            });
        });
    }

    /**
     * Sync tabs and accordion - when clicking accordion, update tab state
     */
    syncTabsWithAccordion() {
        const accordionButtons = document.querySelectorAll('.product-archive-tabs-wrapper .accordion-button');

        accordionButtons.forEach(button => {
            button.addEventListener('click', () => {
                // Get the parent tab pane ID
                const tabPane = button.closest('.tab-pane');
                if (!tabPane) return;

                const tabPaneId = tabPane.getAttribute('id');
                const correspondingTab = document.querySelector(`[href="#${tabPaneId}"]`);

                if (correspondingTab && window.innerWidth >= 768) {
                    correspondingTab.click();
                }
            });
        });
    }

    /**
     * Handle responsive changes between mobile and desktop
     */
    handleResponsiveChanges() {
        let previousWidth = window.innerWidth;

        window.addEventListener('resize', () => {
            const currentWidth = window.innerWidth;

            // Check if we crossed the breakpoint
            if ((previousWidth < 768 && currentWidth >= 768) ||
                (previousWidth >= 768 && currentWidth < 768)) {

                // Reset states when crossing breakpoint
                this.resetStates();
            }

            previousWidth = currentWidth;
        });
    }

    /**
     * Reset all tab and accordion states
     */
    resetStates() {
        document.querySelectorAll('.product-archive-tabs-wrapper').forEach(wrapper => {
            // Activate first tab
            const firstTab = wrapper.querySelector('.nav-link:first-child');
            const firstPane = wrapper.querySelector('.tab-pane:first-child');

            if (firstTab && firstPane) {
                // Reset all tabs
                wrapper.querySelectorAll('.nav-link').forEach(tab => {
                    tab.classList.remove('active');
                    tab.setAttribute('aria-selected', 'false');
                });

                // Reset all panes
                wrapper.querySelectorAll('.tab-pane').forEach(pane => {
                    pane.classList.remove('show', 'active');
                });

                // Activate first
                firstTab.classList.add('active');
                firstTab.setAttribute('aria-selected', 'true');
                firstPane.classList.add('show', 'active');
            }

            // Expand first accordion on mobile
            if (window.innerWidth < 768) {
                const firstAccordionBtn = wrapper.querySelector('.accordion-button:first-child');
                const firstAccordionCollapse = wrapper.querySelector('.accordion-collapse:first-child');

                if (firstAccordionBtn && firstAccordionCollapse) {
                    firstAccordionBtn.classList.remove('collapsed');
                    firstAccordionBtn.setAttribute('aria-expanded', 'true');
                    firstAccordionCollapse.classList.add('show');
                }
            }
        });
    }

    /**
     * Expand an element with animation
     */
    expandElement(element) {
        element.style.height = element.scrollHeight + 'px';
        element.classList.add('collapsing');
        element.classList.remove('collapse');

        setTimeout(() => {
            element.classList.remove('collapsing');
            element.classList.add('collapse', 'show');
            element.style.height = '';
        }, 300);
    }

    /**
     * Collapse an element with animation
     */
    collapseElement(element) {
        element.style.height = element.scrollHeight + 'px';
        element.offsetHeight; // Force reflow
        element.classList.add('collapsing');
        element.classList.remove('collapse', 'show');
        element.style.height = '0';

        setTimeout(() => {
            element.classList.remove('collapsing');
            element.classList.add('collapse');
            element.style.height = '';
        }, 300);
    }

    /**
     * Setup category description "Läs mer" / "Visa mindre" toggle
     */
    setupDescriptionToggle() {
        // Handle "Läs mer" (Read more) click
        const readMoreLinks = document.querySelectorAll('.read-more-link');
        readMoreLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const parent = link.closest('.category-description');
                if (parent) {
                    const truncated = parent.querySelector('.description-truncated');
                    const full = parent.querySelector('.description-full');
                    if (truncated && full) {
                        truncated.classList.add('d-none');
                        full.classList.remove('d-none');
                    }
                }
            });
        });

        // Handle "Visa mindre" (Show less) click
        const readLessLinks = document.querySelectorAll('.read-less-link');
        readLessLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const parent = link.closest('.category-description');
                if (parent) {
                    const truncated = parent.querySelector('.description-truncated');
                    const full = parent.querySelector('.description-full');
                    if (truncated && full) {
                        full.classList.add('d-none');
                        truncated.classList.remove('d-none');
                    }
                }
            });
        });
    }
}

export default ProductArchiveTabs;
